import { gql, useMutation } from '@apollo/client';
import { Modal, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { OrganizationCsvUploadOperator } from '@/graphql-types/globalTypes';
import { useUploadFileToS3 } from '@/hooks/useUploadFileToS3';
import { usePrepareCompanyCsv } from '@/components/admin/sponsorships/Sponsors/components/CompaniesCsvImportModal/hooks/usePrepareCompanyCsv';
import { logError } from '@/imports/logging/ClientLogger';
import { SuccessModal } from '@/imports/ui/admin/components/SuccessModal';
import { FieldValidationError } from '../../../../../../../../client/legacy-shared-modules/csv/csv-field-mapping';
import {
  ValidationFn,
  getValidateCsvFileHeaders,
  normalizeHeader,
} from '../../../../../../../../client/legacy-shared-modules/csv/helpers';
import { CsvImportModalFrameEnhanced } from '@/imports/ui/components/csv/CsvImportModalFrameEnhanced';
import { ICsvImportTemplateProps } from '@/imports/ui/components/csv/CsvImportTemplate';

import {
  OrganizationsCsvArchiveModal_ExecuteOrganizationCsvUpload,
  OrganizationsCsvArchiveModal_ExecuteOrganizationCsvUploadVariables,
} from './graphql-types/OrganizationsCsvArchiveModal_ExecuteOrganizationCsvUpload';

const ORGANIZATION_NAME_HEADER = 'Organization Name';
const BULK_ARCHIVE_MATCH_FIELD = 'name';

const EXECUTE_ORGANIZATION_CSV_UPLOAD = gql`
  mutation OrganizationsCsvArchiveModal_ExecuteOrganizationCsvUpload(
    $input: ExecuteOrganizationCsvUploadInput!
  ) {
    executeOrganizationCsvUpload(input: $input) {
      csvUploadId
    }
  }
`;

const ORGANIZATION_ARCHIVE_TEMPLATE: ICsvImportTemplateProps<
  Record<string, string>
> = {
  filename: 'organization_archive_name_template.csv',
  rows: [{ [ORGANIZATION_NAME_HEADER]: 'Acme Corporation' }],
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export const OrganizationsCsvArchiveModal: React.FC<Props> = ({
  open,
  onClose: onCloseProp,
}) => {
  const [uploadCsvData, { loading: uploadMutationLoading }] = useMutation<
    OrganizationsCsvArchiveModal_ExecuteOrganizationCsvUpload,
    OrganizationsCsvArchiveModal_ExecuteOrganizationCsvUploadVariables
  >(EXECUTE_ORGANIZATION_CSV_UPLOAD);

  const [csvFile, setCsvFile] = React.useState<File | null>(null);
  const [validationErrors, setValidationErrors] =
    React.useState<FieldValidationError | null>(null);
  const [showSuccessModal, setShowSuccessModal] =
    React.useState<boolean>(false);
  const [uploadedCsv, setUploadedCsv] = React.useState<boolean>(false);
  const toast = useToast();

  const {
    prepareCompanyCsv,
    prepareCompanyCsvData,
    prepareCompanyCsvLoading,
    clearCsvResult,
  } = usePrepareCompanyCsv();

  const [uploadFileToS3, { isUploading, error: uploadError, isCanceled }] =
    useUploadFileToS3();

  const prepOrganizationCsvUpload =
    prepareCompanyCsvData?.prepOrganizationCsvUpload;

  const handleUploadFileToS3 = React.useCallback(
    async (file: File): Promise<void> => {
      try {
        if (
          prepOrganizationCsvUpload?.metadataHeaders &&
          prepOrganizationCsvUpload?.presignedS3Url
        ) {
          const newMetadataHeaders = prepOrganizationCsvUpload.metadataHeaders.reduce(
            (obj: Record<string, string>, { name, value }) => (
              (obj[name] = value), obj
            ),
            {}
          );

          await uploadFileToS3({
            file,
            presignedS3Url: prepOrganizationCsvUpload.presignedS3Url,
            metadataHeaders: newMetadataHeaders,
          });
        } else {
          logError(new Error("Can't upload csv file with failed prep"));
        }
      } catch (err) {
        toast({
          title: 'Failed to upload csv. Please try again.',
          status: 'error',
        });
        logError(err);
      }
    },
    [toast, uploadFileToS3, prepOrganizationCsvUpload]
  );

  React.useEffect(() => {
    if (prepareCompanyCsvData && !uploadedCsv && !!csvFile) {
      setUploadedCsv(true);
      handleUploadFileToS3(csvFile).catch((err) => logError(err));
    }
  }, [
    prepareCompanyCsvData,
    uploadedCsv,
    handleUploadFileToS3,
    csvFile,
  ]);

  const handleExecuteOrganizationCsvUpload = async (
    csvUploadId: string
  ): Promise<void> => {
    try {
      await uploadCsvData({
        variables: {
          input: {
            csvUploadId,
            operator: OrganizationCsvUploadOperator.ARCHIVE,
            matchField: BULK_ARCHIVE_MATCH_FIELD,
          },
        },
      });
      setShowSuccessModal(true);
    } catch (err) {
      toast({
        title: 'Failed to execute organization csv archive. Please try again.',
        status: 'error',
      });
      logError(err);
    }
  };

  const getValidateFileFn = (): ((file: File) => Promise<void>) => {
    const validateHeaders: ValidationFn = (headers) => {
      if (!headers.includes(normalizeHeader(ORGANIZATION_NAME_HEADER))) {
        return {
          errors: [`Missing match field: ${ORGANIZATION_NAME_HEADER}`],
        };
      }

      return null;
    };
    return async (file) => {
      await getValidateCsvFileHeaders({
        validateHeaders,
        onSuccess: (successFile) => {
          setValidationErrors(null);
          setCsvFile(successFile);
        },
        onErrors: (errors) => {
          setCsvFile(null);
          setValidationErrors(errors);
        },
      })(file);
    };
  };

  const onClose = (): void => {
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearCsvResult();
    onCloseProp();
  };

  const handleRemoveFile = (e: React.SyntheticEvent): void => {
    e.stopPropagation();
    setValidationErrors(null);
    setCsvFile(null);
    setUploadedCsv(false);
    clearCsvResult();
  };

  const submitFileUpload = async (csvUploadId: string): Promise<void> => {
    await handleExecuteOrganizationCsvUpload(csvUploadId);
    onClose();
  };

  const onFileChange = async (file: File | null): Promise<void> => {
    clearCsvResult();
    await prepareCompanyCsv();
    if (!!file) {
      await getValidateFileFn()(file);
    }
  };

  const hasLoadingSideEffect =
    isUploading || prepareCompanyCsvLoading || uploadMutationLoading;

  const isDisabled =
    isCanceled ||
    !!uploadError ||
    hasLoadingSideEffect ||
    !!(validationErrors && validationErrors.errors) ||
    !csvFile;

  if (showSuccessModal) {
    return (
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Your Bulk Archive is in Progress"
        description="Depending on the size of your file, it might take some time to process."
      />
    );
  }

  return (
    <Modal isOpen={open} onClose={onClose} size="xl">
      <CsvImportModalFrameEnhanced
        pluralEntityName="Organizations"
        modalTitle="Bulk Archive Organizations"
        note="Upload a CSV with an Organization Name column. Matching organizations will be archived."
        footerProps={{
          primaryAction: {
            onClick: async () => {
              if (prepareCompanyCsvData?.prepOrganizationCsvUpload.csvUploadId) {
                await submitFileUpload(
                  prepareCompanyCsvData.prepOrganizationCsvUpload.csvUploadId
                );
              } else {
                logError(new Error('missing csvUploadId'));
              }
            },
            content: 'Archive Organizations',
            loading: hasLoadingSideEffect,
            disabled: isDisabled,
          },
        }}
        template={ORGANIZATION_ARCHIVE_TEMPLATE}
        file={csvFile}
        onFileChange={onFileChange}
        onRemoveFile={handleRemoveFile}
        validationErrors={validationErrors}
        hasLoadingSideEffect={hasLoadingSideEffect}
      />
    </Modal>
  );
};
